/**
 * Defense-in-depth sanitizer for user-supplied text fields on plan/campaign
 * surfaces (project name, goal/description, instructions/notes).
 *
 * React already escapes interpolated text content, so this is NOT about XSS
 * prevention for rendering — it is about keeping persisted values clean:
 *
 * - Strip control characters (C0: U+0000–U+001F + DEL: U+007F + C1: U+0080–U+009F)
 *   which serve no purpose in user content and cause weird rendering, copy/paste
 *   artifacts, and screen-reader instability. \t \n \r are preserved when
 *   `multiline` is true.
 * - Strip Unicode bidi/format overrides (RTL/LTR override etc.) that can be
 *   used for visual spoofing in saved labels.
 * - Trim outer whitespace.
 * - Hard-cap length so a paste of a multi-MB blob doesn't flood the row.
 *
 * Use at the SAVE boundary (just before persistence), not on every keystroke.
 * The input element's `maxLength` handles typing UX; this guards the wire.
 */
export interface SanitizePlanTextOptions {
  maxLength: number;
  multiline?: boolean;
}

// U+200B–U+200F + U+202A–U+202E + U+2066–U+2069 + U+FEFF.
// Zero-width / bidi marks / bidi overrides / isolate format chars / BOM.
const BIDI_FORMAT_CHARS = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

// All control chars. Multiline form preserves \t (U+0009), \n (U+000A), \r (U+000D).
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_SINGLELINE = /[\x00-\x1F\x7F-\x9F]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_MULTILINE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g;

export function sanitizePlanText(raw: string, opts: SanitizePlanTextOptions): string {
  if (typeof raw !== 'string') return '';
  const controlPattern = opts.multiline ? CONTROL_CHARS_MULTILINE : CONTROL_CHARS_SINGLELINE;
  return raw
    .replace(controlPattern, '')
    .replace(BIDI_FORMAT_CHARS, '')
    .trim()
    .slice(0, opts.maxLength);
}

export const PLAN_NAME_MAX_LENGTH = 120;
export const PLAN_GOAL_MAX_LENGTH = 2000;
export const PLAN_NOTES_MAX_LENGTH = 4000;
